import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { api, imgSrc } from "../lib/api";
import { PageHero } from "../components/PageHero";
import { FadeUp, SectionHead } from "../components/Reveal";
import { CtaSection } from "../components/CtaSection";
import { Pagination } from "../components/Pagination";

const ArticleMeta = ({ a }) => (
  <p className="text-[11px] font-medium uppercase tracking-[0.3em] text-gold">
    {a.kategori} — {a.tanggal} · {a.waktu_baca}
  </p>
);

const Featured = ({ a }) => (
  <FadeUp>
    <Link
      to={`/artikel/${a.slug}`}
      data-testid={`article-featured-${a.slug}`}
      className="group grid gap-10 border border-white/10 bg-[#0d0d0d] lg:grid-cols-2"
    >
      <div className="overflow-hidden">
        <motion.img
          src={imgSrc(a.gambar)}
          alt={a.judul}
          className="h-full min-h-[280px] w-full object-cover"
          whileHover={{ scale: 1.04 }}
          transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
        />
      </div>
      <div className="flex flex-col justify-center p-8 lg:p-14">
        <ArticleMeta a={a} />
        <h2 className="mt-5 font-serif text-3xl leading-snug text-[#F5F5F5] transition-colors duration-300 group-hover:text-gold-light lg:text-4xl">
          {a.judul}
        </h2>
        <p className="mt-5 text-base font-light leading-relaxed text-[#A3A3A3]">
          {a.ringkasan}
        </p>
        <span className="mt-8 inline-flex items-center gap-2 text-[12px] font-semibold uppercase tracking-[0.25em] text-gold">
          Baca artikel
          <ArrowUpRight
            size={15}
            className="transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5"
          />
        </span>
      </div>
    </Link>
  </FadeUp>
);

const PER_PAGE = 6;

export default function Artikel() {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const listRef = useRef(null);

  useEffect(() => {
    api
      .get("/articles")
      .then((r) => setArticles(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const featured = page === 1 ? articles[0] : null;
  const pool = articles.slice(1);
  const totalPages = Math.max(1, Math.ceil(pool.length / PER_PAGE));
  const gridItems = pool.slice((page - 1) * PER_PAGE, page * PER_PAGE);

  const goToPage = (n) => {
    setPage(n);
    listRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <>
      <PageHero
        eyebrow="Artikel & Wawasan"
        titleLines={[
          "Hukum, dijelaskan",
          <span key="t" className="italic text-gold">
            dengan bahasa manusia.
          </span>,
        ]}
        description="Ulasan hukum praktis dari para advokat kami — agar Anda memahami hak sebelum membutuhkannya."
      />

      <section ref={listRef} className="mx-auto w-full max-w-7xl scroll-mt-28 px-6 pb-28 lg:px-10">
        {loading ? (
          <p data-testid="articles-loading" className="py-20 text-center font-serif text-2xl italic text-[#737373]">
            Memuat artikel…
          </p>
        ) : articles.length === 0 ? (
          <p data-testid="articles-empty" className="py-20 text-center font-serif text-2xl italic text-[#737373]">
            Belum ada artikel yang diterbitkan.
          </p>
        ) : (
          <>
            {featured && <Featured a={featured} />}
            <div className="mt-16 grid gap-10 sm:grid-cols-2 lg:grid-cols-3">
              {gridItems.map((a, i) => (
                <FadeUp key={a.id} delay={0.08 * (i % 3)}>
                  <Link
                    to={`/artikel/${a.slug}`}
                    data-testid={`article-card-${a.slug}`}
                    className="group block"
                  >
                    <div className="overflow-hidden border border-white/10">
                      <motion.img
                        src={imgSrc(a.gambar)}
                        alt={a.judul}
                        className="aspect-[16/10] w-full object-cover"
                        whileHover={{ scale: 1.05 }}
                        transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
                      />
                    </div>
                    <div className="mt-6">
                      <ArticleMeta a={a} />
                      <h3 className="mt-3 font-serif text-2xl leading-snug text-[#F5F5F5] transition-colors duration-300 group-hover:text-gold-light">
                        {a.judul}
                      </h3>
                      <p className="mt-3 line-clamp-2 text-sm font-light leading-relaxed text-[#A3A3A3]">
                        {a.ringkasan}
                      </p>
                    </div>
                  </Link>
                </FadeUp>
              ))}
            </div>
            <Pagination page={page} totalPages={totalPages} onChange={goToPage} testidPrefix="artikel" />
          </>
        )}
      </section>

      <CtaSection
        testid="artikel-cta"
        lines={[
          "Punya pertanyaan hukum?",
          <span key="t" className="italic text-gold">
            Tanyakan langsung.
          </span>,
        ]}
      />
    </>
  );
}
